import React, { createContext, useContext, useState, useEffect } from 'react';

// Default OVA branding (main platform)
const DEFAULT_BRANDING = {
  tenant_id: null,
  company_name: 'OVA',
  subdomain: null,
  logo_url: '/ova-logo-transparent-192.png',
  primary_color: '#4DC9F6',      // Sky Blue (OVA brand)
  secondary_color: '#7B2D8E',    // Purple (OVA brand)
  accent_color: '#ffffff',       // White
  support_email: 'support@ova.ai',
  support_phone: null,
  tagline: 'Optimized Vehicle Assistance',
  is_platform: true  // This is the main platform, not a tenant
};

// Platform domain configuration
const PLATFORM_DOMAINS = ['ovadispatch.com', 'www.ovadispatch.com', 'localhost', 'preview.emergentagent.com'];

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    detectAndLoadTenant();
  }, []);

  const detectAndLoadTenant = async () => {
    try {
      setLoading(true);
      
      const hostname = window.location.hostname;
      let tenantIdentifier = null;
      let isCustomDomain = false;
      
      // Method 1: Check if this is a custom domain
      // Custom domains are NOT subdomains of ovadispatch.com
      const isPlatformDomain = PLATFORM_DOMAINS.some(d => 
        hostname === d || hostname.endsWith(`.${d}`)
      );
      
      if (!isPlatformDomain && !hostname.includes('localhost')) {
        // This is a custom domain - look it up
        isCustomDomain = true;
        try {
          const response = await fetch(`${backendUrl}/api/domains/lookup?domain=${encodeURIComponent(hostname)}`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.tenant_id) {
              tenantIdentifier = data.tenant_id;
            }
          }
        } catch (e) {
          console.warn('Custom domain lookup failed:', e);
        }
      }
      
      // Method 2: Check subdomain (e.g., ova.ovadispatch.com)
      if (!tenantIdentifier && isPlatformDomain) {
        const parts = hostname.split('.');
        if (parts.length >= 3) {
          const subdomain = parts[0];
          // Skip 'www' and 'app' subdomains
          if (subdomain !== 'www' && subdomain !== 'app') {
            tenantIdentifier = subdomain;
          }
        }
      }
      
      // Method 3: Check URL query parameter (for development/testing)
      // e.g., /driver?tenant=ova
      if (!tenantIdentifier) {
        const urlParams = new URLSearchParams(window.location.search);
        tenantIdentifier = urlParams.get('tenant');
      }
      
      // Method 4: Check localStorage for remembered tenant
      if (!tenantIdentifier) {
        tenantIdentifier = localStorage.getItem('ova_tenant');
      }
      
      if (tenantIdentifier) {
        // Fetch tenant configuration from backend
        const response = await fetch(`${backendUrl}/api/saas/tenant/${tenantIdentifier}`);
        
        if (response.ok) {
          const tenantData = await response.json();
          
          const tenantBranding = {
            tenant_id: tenantData.tenant_id,
            company_name: tenantData.branding?.company_name_display || tenantData.company_name,
            subdomain: tenantData.subdomain,
            custom_domain: tenantData.custom_domain,
            logo_url: tenantData.branding?.logo_url,
            primary_color: tenantData.branding?.primary_color || '#3B82F6',
            secondary_color: tenantData.branding?.secondary_color || '#1E40AF',
            accent_color: tenantData.branding?.accent_color || '#ffffff',
            support_email: tenantData.branding?.support_email || tenantData.admin_email,
            support_phone: tenantData.branding?.support_phone || tenantData.phone,
            tagline: tenantData.branding?.tagline || 'Transportation Services',
            is_platform: false,
            plan: tenantData.plan,
            features: tenantData.features
          };
          
          setTenant(tenantBranding);
          localStorage.setItem('ova_tenant', tenantIdentifier);
          
          // Apply tenant's custom CSS colors
          applyTenantColors(tenantBranding);
        } else {
          // Tenant not found, use default
          console.warn(`Tenant '${tenantIdentifier}' not found, using default branding`);
          setTenant(DEFAULT_BRANDING);
          localStorage.removeItem('ova_tenant');
        }
      } else {
        // No tenant specified, use platform branding
        setTenant(DEFAULT_BRANDING);
        applyTenantColors(DEFAULT_BRANDING);
      }
    } catch (err) {
      console.error('Error loading tenant:', err);
      setError(err.message);
      setTenant(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  };

  const applyTenantColors = (branding) => {
    // Apply tenant colors as CSS variables
    const root = document.documentElement;
    root.style.setProperty('--tenant-primary', branding.primary_color);
    root.style.setProperty('--tenant-secondary', branding.secondary_color);
    root.style.setProperty('--tenant-accent', branding.accent_color);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', branding.primary_color);
    }
  };

  const switchTenant = async (tenantIdentifier) => {
    // Allow switching tenants (for admin/testing)
    if (tenantIdentifier) {
      localStorage.setItem('ova_tenant', tenantIdentifier);
    } else {
      localStorage.removeItem('ova_tenant');
    }
    await detectAndLoadTenant();
  };

  const clearTenant = () => {
    localStorage.removeItem('ova_tenant');
    setTenant(DEFAULT_BRANDING);
    applyTenantColors(DEFAULT_BRANDING);
  };

  const value = {
    tenant,
    loading,
    error,
    switchTenant,
    clearTenant,
    isPlatform: tenant.is_platform,
    tenantId: tenant.tenant_id,
    // Helper for API calls - always include tenant_id
    getApiHeaders: () => ({
      'Content-Type': 'application/json',
      ...(tenant.tenant_id && { 'X-Tenant-ID': tenant.tenant_id })
    })
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
